import { Combobox, Transition } from '@headlessui/react';
import { Fragment, useState, useMemo } from 'react';
import { Search, Check, Plus, Package } from 'lucide-react';

/**
 * Combobox moderno para seleccionar o crear folios
 * Incluye búsqueda en tiempo real y opción de crear nuevo folio
 */
export default function FolioCombobox({
  folios = [],
  value = '',
  onChange,
  onCreateNew,
  disabled = false,
  placeholder = "Buscar o crear folio..."
}) {
  const [query, setQuery] = useState('');

  // Filtrar folios en tiempo real basado en la búsqueda
  const filteredFolios = useMemo(() => {
    if (query === '') return folios;

    const normalizedQuery = query.toLowerCase();
    return folios.filter((folio) =>
      folio.toLowerCase().includes(normalizedQuery)
    );
  }, [query, folios]);

  const handleSelect = (selectedValue) => {
    if (selectedValue === '__create_new__') {
      onCreateNew(query);
    } else {
      onChange(selectedValue);
    }
  };

  return (
    <Combobox value={value} onChange={handleSelect} disabled={disabled}>
      <div className="relative">
        {/* Input de búsqueda */}
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
            size={16}
          />

          <Combobox.Input
            className="w-full px-3 py-2 pl-9 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500 focus:border-ink-500 focus:outline-none bg-white text-ink-900 placeholder-ink-400 disabled:bg-ink-50 disabled:cursor-not-allowed"
            displayValue={(folio) => folio}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
          />
        </div>

        {/* Dropdown con opciones */}
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Combobox.Options className="absolute z-50 mt-2 w-full bg-white border border-ink-200 rounded-lg shadow-lifted max-h-80 overflow-auto">
            {/* Opción para crear nuevo folio */}
            {query.length > 0 && (
              <>
                <Combobox.Option
                  value="__create_new__"
                  className={({ active }) => `
                    flex items-center gap-3 px-4 py-3
                    bg-blue-50 border-l-4 border-blue-500
                    cursor-pointer transition-colors
                    ${active ? 'bg-blue-100' : ''}
                  `}
                >
                  {({ active }) => (
                    <>
                      <Plus size={18} className="text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-blue-900 truncate">
                          Crear "{query}" como nuevo folio
                        </div>
                        <div className="text-xs text-blue-600">
                          Nuevo folio con autocompletado activado
                        </div>
                      </div>
                    </>
                  )}
                </Combobox.Option>

                {/* Divider si hay resultados */}
                {filteredFolios.length > 0 && (
                  <div className="border-b-2 border-ink-200" />
                )}
              </>
            )}

            {/* Lista de folios existentes */}
            {filteredFolios.length === 0 && query !== '' ? (
              // Sin resultados
              <div className="px-4 py-8 text-center text-ink-500">
                <Package size={32} className="mx-auto mb-2 opacity-30" />
                <p className="font-medium">No se encontraron folios</p>
                <p className="text-xs mt-1">
                  Usa la opción de arriba para crear uno nuevo
                </p>
              </div>
            ) : filteredFolios.length === 0 && query === '' ? (
              // Lista vacía inicial
              <div className="px-4 py-6 text-center text-ink-500">
                <Package size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No hay folios registrados</p>
                <p className="text-xs mt-1">
                  Escribe para crear el primer folio
                </p>
              </div>
            ) : (
              // Folios filtrados
              filteredFolios.map((folio) => (
                <Combobox.Option
                  key={folio}
                  value={folio}
                  className={({ active }) => `
                    flex items-center justify-between px-4 py-3
                    cursor-pointer transition-colors
                    border-b border-ink-100 last:border-b-0
                    ${active ? 'bg-accent/5 border-l-4 border-l-accent' : 'border-l-4 border-l-transparent'}
                  `}
                >
                  {({ selected, active }) => (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium truncate ${selected ? 'text-accent' : 'text-ink-900'}`}>
                          {folio}
                        </div>
                      </div>

                      {selected && (
                        <Check size={18} className="text-accent flex-shrink-0 ml-2" />
                      )}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}
